import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../../services/firebase/notification.service';
import { Notification } from '../../types/notification';

// Define the state interface
interface NotificationState {
  notifications: Notification[];         // All notifications merged
  systemNotifications: Notification[];    // System-generated notifications
  additionalNotifications: Notification[]; // Additional notifications
  unreadCount: number;                   // Regular unread count
  unreadSysCount: number;                // System unread count
  unreadAddCount: number;                // Additional unread count
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  oldestTimestamp: number | null;
  isRealTimeConnected: boolean;          // Real-time subscription status
}

// Initial state
const initialState: NotificationState = {
  notifications: [],
  systemNotifications: [],
  additionalNotifications: [],
  unreadCount: 0,
  unreadSysCount: 0,
  unreadAddCount: 0,
  isLoading: false,
  error: null,
  hasMore: true,
  oldestTimestamp: null,
  isRealTimeConnected: false
};

// Async thunks
export const fetchNotifications = createAsyncThunk<
  Notification[],
  { userId: string; limit?: number },
  { rejectValue: string }
>('notifications/fetchNotifications', async ({ userId, limit }, { rejectWithValue }) => {
  try {
    const notifications = await getUserNotifications(userId, limit);
    return notifications;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch notifications';
    return rejectWithValue(errorMessage);
  }
});

export const fetchMoreNotifications = createAsyncThunk<
  Notification[],
  { userId: string; limit?: number; startAfterTimestamp: number },
  { rejectValue: string }
>('notifications/fetchMoreNotifications', async ({ userId, limit, startAfterTimestamp }, { rejectWithValue }) => {
  try {
    const notifications = await getUserNotifications(userId, limit, startAfterTimestamp);
    return notifications;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch more notifications';
    return rejectWithValue(errorMessage);
  }
});

export const fetchUnreadCount = createAsyncThunk<
  number,
  string,
  { rejectValue: string }
>('notifications/fetchUnreadCount', async (userId, { rejectWithValue }) => {
  try {
    const count = await getUnreadNotificationCount(userId);
    return count;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch unread count';
    return rejectWithValue(errorMessage);
  }
});

export const readNotification = createAsyncThunk<
  { userId: string; notificationId: string; notificationType?: 'regular' | 'system' | 'additional' },
  { userId: string; notificationId: string; notificationType?: 'regular' | 'system' | 'additional' },
  { rejectValue: string }
>('notifications/readNotification', async ({ userId, notificationId, notificationType }, { rejectWithValue }) => {
  try {
    await markNotificationAsRead(userId, notificationId, notificationType);
    return { userId, notificationId, notificationType };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to mark notification as read';
    return rejectWithValue(errorMessage);
  }
});

export const readAllNotifications = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('notifications/readAllNotifications', async (userId, { rejectWithValue }) => {
  try {
    await markAllNotificationsAsRead(userId);
    return userId;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to mark all notifications as read';
    return rejectWithValue(errorMessage);
  }
});

export const removeNotification = createAsyncThunk<
  { userId: string; notificationId: string; notificationType?: 'regular' | 'system' | 'additional' },
  { userId: string; notificationId: string; notificationType?: 'regular' | 'system' | 'additional' },
  { rejectValue: string }
>('notifications/removeNotification', async ({ userId, notificationId, notificationType }, { rejectWithValue }) => {
  try {
    await deleteNotification(userId, notificationId, notificationType);
    return { userId, notificationId, notificationType };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete notification';
    return rejectWithValue(errorMessage);
  }
});

// Create the slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    setUnreadSysCount: (state, action: PayloadAction<number>) => {
      state.unreadSysCount = action.payload;
    },
    setUnreadAddCount: (state, action: PayloadAction<number>) => {
      state.unreadAddCount = action.payload;
    },
    setRealTimeNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    setSystemNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.systemNotifications = action.payload;
    },
    setAdditionalNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.additionalNotifications = action.payload;
    },
    setRealTimeConnected: (state, action: PayloadAction<boolean>) => {
      state.isRealTimeConnected = action.payload;
    },
    resetNotifications: (state) => {
      state.notifications = [];
      state.systemNotifications = [];
      state.additionalNotifications = [];
      state.unreadCount = 0;
      state.unreadSysCount = 0;
      state.unreadAddCount = 0;
      state.hasMore = true;
      state.oldestTimestamp = null;
      state.isRealTimeConnected = false;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.isLoading = false;
      state.notifications = action.payload;
      state.hasMore = action.payload.length > 0;
      
      // Set oldest timestamp for pagination
      if (action.payload.length > 0) {
        const timestamps = action.payload.map(n => n.dateCreated);
        state.oldestTimestamp = Math.min(...timestamps);
      } else {
        state.oldestTimestamp = null;
      }
    });
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch notifications';
    });
    
    // Fetch more notifications
    builder.addCase(fetchMoreNotifications.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchMoreNotifications.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Add new notifications to the existing ones, ensuring no duplicates
      const existingIds = new Set(state.notifications.map(n => n.id));
      const newNotifications = action.payload.filter(n => !existingIds.has(n.id));
      
      state.notifications = [...state.notifications, ...newNotifications];
      state.hasMore = newNotifications.length > 0;
      
      // Update oldest timestamp for pagination
      if (newNotifications.length > 0) {
        const timestamps = newNotifications.map(n => n.dateCreated);
        state.oldestTimestamp = Math.min(...timestamps);
      }
    });
    builder.addCase(fetchMoreNotifications.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch more notifications';
    });
    
    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload;
    });
    
    // Mark notification as read
    builder.addCase(readNotification.fulfilled, (state, action) => {
      const { notificationId, notificationType } = action.payload;
      
      // Update the notification in the correct list
      let notificationList: Notification[] = [];
      let unreadCountKey: 'unreadCount' | 'unreadSysCount' | 'unreadAddCount' = 'unreadCount';
      
      if (notificationType === 'system') {
        notificationList = state.systemNotifications;
        unreadCountKey = 'unreadSysCount';
      } else if (notificationType === 'additional') {
        notificationList = state.additionalNotifications;
        unreadCountKey = 'unreadAddCount';
      } else {
        notificationList = state.notifications;
        unreadCountKey = 'unreadCount';
      }
      
      // Update the notification
      const notification = notificationList.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
        // Also update the legacy field if present
        if ('isRead' in notification) {
          (notification as any).isRead = true;
        }
      }
      
      // Decrement the appropriate unread count
      if (state[unreadCountKey] > 0) {
        state[unreadCountKey] -= 1;
      }
    });
    
    // Mark all notifications as read
    builder.addCase(readAllNotifications.fulfilled, (state) => {
      // Update all notifications in all lists
      state.notifications.forEach(notification => {
        notification.read = true;
        // Also update the legacy field if present
        if ('isRead' in notification) {
          (notification as any).isRead = true;
        }
      });
      
      state.systemNotifications.forEach(notification => {
        notification.read = true;
        // Also update the legacy field if present
        if ('isRead' in notification) {
          (notification as any).isRead = true;
        }
      });
      
      state.additionalNotifications.forEach(notification => {
        notification.read = true;
        // Also update the legacy field if present
        if ('isRead' in notification) {
          (notification as any).isRead = true;
        }
      });
      
      // Reset all unread counts
      state.unreadCount = 0;
      state.unreadSysCount = 0;
      state.unreadAddCount = 0;
    });
    
    // Remove notification
    builder.addCase(removeNotification.fulfilled, (state, action) => {
      const { notificationId, notificationType } = action.payload;
      
      // Determine which list to remove from
      let notificationList: Notification[] = [];
      let unreadCountKey: 'unreadCount' | 'unreadSysCount' | 'unreadAddCount' = 'unreadCount';
      
      if (notificationType === 'system') {
        notificationList = state.systemNotifications;
        unreadCountKey = 'unreadSysCount';
      } else if (notificationType === 'additional') {
        notificationList = state.additionalNotifications;
        unreadCountKey = 'unreadAddCount';
      } else {
        notificationList = state.notifications;
        unreadCountKey = 'unreadCount';
      }
      
      // Check if the notification being removed was unread
      const notification = notificationList.find(n => n.id === notificationId);
      const wasUnread = notification && (!notification.read && !('isRead' in notification && notification.isRead));
      
      // Remove the notification from the correct list
      if (notificationType === 'system') {
        state.systemNotifications = state.systemNotifications.filter(n => n.id !== notificationId);
      } else if (notificationType === 'additional') {
        state.additionalNotifications = state.additionalNotifications.filter(n => n.id !== notificationId);
      } else {
        state.notifications = state.notifications.filter(n => n.id !== notificationId);
      }
      
      // Decrement the appropriate unread count if it was unread
      if (wasUnread && state[unreadCountKey] > 0) {
        state[unreadCountKey] -= 1;
      }
    });
  }
});

// Export actions
export const { 
  setUnreadCount, 
  setUnreadSysCount,
  setUnreadAddCount,
  setRealTimeNotifications,
  setSystemNotifications,
  setAdditionalNotifications,
  setRealTimeConnected,
  resetNotifications, 
  clearError 
} = notificationSlice.actions;

// Export reducer
export default notificationSlice.reducer;